import { IconButton } from '@/ui/common/Buttons/IconButton';
import { ClockIcon } from '@/ui/common/Icons/ClockIcon';
import { InfoIcon } from '@/ui/common/Icons/InfoIcon';
import { Tooltip } from '@/ui/common/Tooltip/Tooltip';
import { CustomInput } from '@/ui/common/Inputs/CustomInput';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';
import { TTicketNames } from '@/domain/Activities.domain';

type TRentalCounterProps = {
    id: string;
    name: string;
    duration: string;
    price: string;
    count: number;
    min: number;
    handleCount: (arg: number) => void;
    needTicketNames: boolean;
    ticketNames: TTicketNames;
    handleTicketNamesAdd: (ticket_id: string, guest_id: number) => void;
    handleTicketNamesRemove: (ticket_id: string, guest_id: number) => void;
    handleTicketNamesChange: (
        ticket_id: string,
        guest_id: number,
        name: string,
        value: string
    ) => void;
    infoText?: string;
    notAllowedText?: string;
    notAllowed?: boolean;
};

export const RentalCounter = ({
    id,
    name,
    duration,
    price,
    count,
    handleCount,
    needTicketNames,
    ticketNames,
    handleTicketNamesAdd,
    handleTicketNamesRemove,
    handleTicketNamesChange,
    infoText = '',
    notAllowedText = '',
    notAllowed,
    min,
}: TRentalCounterProps) => (
    <div data-test-id="tickets-counter" className="flex max-w-full flex-col items-center px-2">
        <div className="mb-4 flex w-full max-w-max items-center justify-between">
            <div className="flex w-full items-center gap-3 overflow-hidden">
                <div className="tx-large-medium flex overflow-x-hidden whitespace-nowrap text-gray-1 dark:text-white">
                    <p className="max-w-full overflow-hidden overflow-ellipsis">{name}</p>
                    &nbsp;
                    <p>{`- ${getFormattedPrice(price)}`}</p>
                </div>
            </div>
            {!!infoText && (
                <Tooltip
                    text={infoText}
                    className="ml-3"
                    tooltipClassName="left-auto !-right-7"
                    data-test-id="counter-tooltip"
                >
                    <div className="flex h-3 w-3 items-center justify-center rounded-full">
                        <InfoIcon className="[&_circle]:dark:stroke-gray-3 [&_path]:dark:fill-gray-3" />
                    </div>
                </Tooltip>
            )}
            {!!duration && (
                <div className="ml-6 flex items-center">
                    <ClockIcon className="[&_path]:dark:fill-gray-3" />
                    <span
                        data-test-id="counter-hrs"
                        className="tx-large-regular whitespace-nowrap text-gray-2 dark:text-gray-3"
                    >
                        {duration}
                    </span>
                </div>
            )}
        </div>
        <div className="tx-heading-1 flex w-full max-w-max items-center md:tx-heading-2">
            <IconButton
                icon=""
                className="mx-0 h-12 w-12 max-w-none rounded-full bg-gray-3 bg-gradient-to-r from-blue-4 to-blue-12 p-0 text-white opacity-70 hover:shadow-lightButton disabled:bg-none disabled:shadow-none hover:disabled:bg-none dark:bg-dark-1"
                label="-"
                onClick={() => {
                    handleCount(count !== min ? count - 1 : 0);
                    if (needTicketNames) handleTicketNamesRemove(id, count !== min ? count - 1 : 0);
                }}
                disabled={count <= 0}
                data-test-id="counter-min"
            />
            <div
                className={`mx-3 flex h-12 w-28 flex-col items-center justify-center rounded-full opacity-70 ${
                    count <= 0
                        ? 'bg-white dark:bg-dark-3'
                        : 'bg-gradient-to-r from-blue-4 to-blue-12'
                }`}
            >
                <span
                    data-test-id="counter-value"
                    className={`tx-heading-2 ${count <= 0 ? 'text-gray-3' : 'text-white'}`}
                >
                    {count}
                </span>
            </div>
            <Tooltip
                text={notAllowed ? notAllowedText : ''}
                tooltipClassName=" bottom-auto -top-32 left-auto -right-3 md:-top-28 md:-right-2"
            >
                <IconButton
                    icon=""
                    className="mx-0 h-12 w-12 max-w-none rounded-full bg-gray-3 bg-gradient-to-r from-blue-4 to-blue-12 p-0 text-white opacity-70 hover:shadow-lightButton disabled:bg-none disabled:shadow-none hover:disabled:bg-none dark:bg-dark-1"
                    label="+"
                    onClick={() => {
                        handleCount(count < min ? min : count + 1);
                        if (needTicketNames)
                            handleTicketNamesAdd(id, count < min ? min : count + 1);
                    }}
                    disabled={notAllowed}
                    data-test-id="counter-plus"
                />
            </Tooltip>
        </div>
        {!!min && min > 1 && (
            <span
                data-test-id="counter-hrs"
                className="tx-medium-medium mt-2 whitespace-nowrap text-gray-2 dark:text-gray-3"
            >
                {`${min} ticket minimum`}
            </span>
        )}
        {needTicketNames && (
            <div className="flex w-full flex-col gap-1">
                {ticketNames[id]
                    ? Object.entries(ticketNames[id])?.map(
                          ([guest_id, { first_name, last_name }]) => (
                              <div
                                  key={guest_id}
                                  className="flex gap-6 lg:items-start lg:justify-between lg:gap-7"
                                  data-test-id="counter-guest-names"
                              >
                                  <CustomInput
                                      className="max-w-full lg:max-w-[332px]"
                                      name="first_name"
                                      required
                                      label={`Ticket ${guest_id} - First Name`}
                                      value={first_name}
                                      onChange={(e) =>
                                          handleTicketNamesChange(
                                              id,
                                              +guest_id,
                                              e.target.name,
                                              e.target.value
                                          )
                                      }
                                      data-test-id="guest-first-name"
                                  />
                                  <CustomInput
                                      className="max-w-full lg:max-w-[332px]"
                                      name="last_name"
                                      required
                                      label={`Ticket ${guest_id} - Last Name`}
                                      value={last_name}
                                      onChange={(e) =>
                                          handleTicketNamesChange(
                                              id,
                                              +guest_id,
                                              e.target.name,
                                              e.target.value
                                          )
                                      }
                                      data-test-id="guest-second-name"
                                  />
                              </div>
                          )
                      )
                    : null}
            </div>
        )}
    </div>
);
